import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { User } from '@supabase/supabase-js';
import { AnalyticsService } from '../../services/analytics.service';
import { SeoService } from '../../services/seo.service';
import { SupabaseService } from '../../services/supabase.service';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslatePipe],
  templateUrl: './landing-partner.component.html',
  styleUrl: './landing-partner.component.scss',
})
export class LandingPartnerComponent implements OnInit {
  @ViewChild('heroCta') heroCta!: ElementRef;
  user: User | null = null;
  currentYear = new Date().getFullYear();
  clients: any[];
  /** clients repeated + doubled into two identical halves for the seamless marquee. */
  marqueeClients: any[] = [];

  /** Drives the persistent floating CTA — shown once the hero CTA scrolls away. */
  showSticky = false;

  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private supabaseService: SupabaseService,
    private analytics: AnalyticsService,
    private seo: SeoService,
    private translate: TranslateService
  ) {
    this.clients = [
      { name: 'Beachange', logo: 'aeik-logo.png' },
      { name: 'KSpersonal', logo: 'kspersonal-logo.png' },
      { name: 'Training Room', logo: 'trainingroom-logo.png' },
      { name: 'Alpha 25', logo: 'alpha25-logo.png' },
    ];
    // Marquee carousel: repeat to fill wide viewports, then double into two
    // identical halves so the -50% CSS scroll loops with no visible seam.
    const half = [...this.clients, ...this.clients];
    this.marqueeClients = [...half, ...half];
  }

  ngOnInit() {
    // Club-owner-targeted SEO. Covers BOTH products: get discovered (free
    // listing + visit requests) AND the full online booking & management system.
    this.seo.setMeta({
      title: this.translate.instant('partners.metaTitle'),
      // ~155 chars so it isn't truncated in the SERP; front-loads the value.
      description: this.translate.instant('partners.metaDescription'),
      type: 'website',
      // Both '/' and '/partners' render this component — canonicalize both to the
      // root so /partners doesn't self-canonicalize into duplicate content.
      url: 'https://actively.gr/',
    });

    // Brand identity (Organization + WebSite) is emitted ONCE, statically, from
    // index.html's @graph — it is present on every route, so the page adds no
    // duplicate of its own. This page only owns the FAQPage below.

    // FAQPage structured data (mirrors the on-page FAQ verbatim) for rich
    // results + AI/LLM citation. Removed on navigation away.
    this.seo.setJsonLd('partners-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: this.translate.instant('partners.faq1Q'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.translate.instant('partners.faq1A'),
          },
        },
        {
          '@type': 'Question',
          name: this.translate.instant('partners.faq2Q'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.translate.instant('partners.faq2A'),
          },
        },
        {
          '@type': 'Question',
          name: this.translate.instant('partners.faq3Q'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.translate.instant('partners.faq3A'),
          },
        },
        {
          '@type': 'Question',
          name: this.translate.instant('partners.faq4Q'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.translate.instant('partners.faq4A'),
          },
        },
      ],
    });
    this.destroyRef.onDestroy(() => this.seo.removeJsonLd('partners-faq'));

    this.supabaseService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.user = user;
      });
  }

  /**
   * Show the floating CTA once the hero CTA block has scrolled out of view, so
   * the action is reachable at every scroll point without covering the hero.
   * @HostListener is zone-patched, so change detection runs reliably (unlike a
   * raw IntersectionObserver, which zone.js does not patch).
   */
  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScroll() {
    const cta = this.heroCta?.nativeElement;
    if (!cta) return;
    const past = cta.getBoundingClientRect().bottom < 0;
    if (past !== this.showSticky) {
      this.showSticky = past;
    }
  }

  /**
   * Every "list your facility" CTA goes STRAIGHT to club signup — no lead
   * form in between. Asking for email + club name before the account only
   * duplicated what signup and the wizard collect anyway.
   */
  startSignup(source: string) {
    // Consent-gated; no PII in props.
    this.analytics.track('signup_started', { source });
    // This is the "For clubs" page, so the user IS a club owner — go straight
    // to club signup, skipping the athlete/club role chooser.
    this.router.navigate(['/auth/signup/club']);
  }

  contact() {
    // Supply-side intent: owner heading to the contact / book-a-call flow.
    this.analytics.track('call_booked', { source: 'partners' });
    this.router.navigate(['/contact']);
  }
}
